"use client";

import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import type { EmailAgentConfig } from "./use-agents";

export type { EmailAgentConfig } from "./use-agents";

interface ConfigResponse {
  agent_id: string;
  agent_name: string;
  config: EmailAgentConfig;
}

export function useEmailAgentConfig(agentId: string | null) {
  const [config, setConfig] = useState<EmailAgentConfig>({});
  const [agentName, setAgentName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth, isAuthenticated } = useAuth();

  const loadConfig = useCallback(async () => {
    if (!agentId) {
      return;
    }

    if (!isAuthenticated) {
      // Wait for auth - don't show empty state yet
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/api/agents/email/${agentId}/config`);

      if (response.ok) {
        const data: ConfigResponse = await response.json();
        setConfig(data.config || {});
        setAgentName(data.agent_name);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to load config");
      }
    } catch (err) {
      console.error("Error loading email agent config:", err);
      setError("Failed to load config");
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAuthenticated, agentId]);

  const saveConfig = useCallback(
    async (newConfig: Partial<EmailAgentConfig>): Promise<EmailAgentConfig> => {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }

      setIsSaving(true);
      setError(null);

      try {
        const response = await fetchWithAuth(`/api/agents/email/${agentId}/config`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ config: newConfig }),
        });

        if (response.ok) {
          const data: ConfigResponse = await response.json();
          setConfig(data.config);
          return data.config;
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to save config");
        }
      } finally {
        setIsSaving(false);
      }
    },
    [fetchWithAuth, agentId],
  );

  // All update helpers send ONLY the partial delta to the server.
  // The server PATCH handler deep-merges each section with the existing config,
  // so spreading local state here would risk overwriting fresh server values
  // with stale client state (e.g., re-disabling auto_reply.enabled).

  const updateAutoReply = useCallback(
    async (autoReply: Partial<NonNullable<EmailAgentConfig["auto_reply"]>>) => {
      return saveConfig({
        auto_reply: autoReply as EmailAgentConfig["auto_reply"],
      });
    },
    [saveConfig],
  );

  const updateHumanHandover = useCallback(
    async (handover: Partial<NonNullable<EmailAgentConfig["human_handover"]>>) => {
      return saveConfig({
        human_handover: handover as EmailAgentConfig["human_handover"],
      });
    },
    [saveConfig],
  );

  const updateEmailForward = useCallback(
    async (forward: Partial<NonNullable<EmailAgentConfig["email_forward"]>>) => {
      return saveConfig({
        email_forward: forward as EmailAgentConfig["email_forward"],
      });
    },
    [saveConfig],
  );

  const updateCrmIntegration = useCallback(
    async (crm: Partial<NonNullable<EmailAgentConfig["crm_integration"]>>) => {
      return saveConfig({
        crm_integration: crm as EmailAgentConfig["crm_integration"],
      });
    },
    [saveConfig],
  );

  const updateIdentityOverrides = useCallback(
    async (overrides: EmailAgentConfig["identity_overrides"]) => {
      return saveConfig({ identity_overrides: overrides });
    },
    [saveConfig],
  );

  return {
    config,
    agentName,
    isLoading,
    isSaving,
    error,
    loadConfig,
    saveConfig,
    updateAutoReply,
    updateHumanHandover,
    updateEmailForward,
    updateCrmIntegration,
    updateIdentityOverrides,
  };
}
